import {
  Add,
  CheckBox,
  ContentCopy,
  ControlPointDuplicate,
  Delete,
  Favorite,
  InsertLink,
  MoreVert,
  OpenInNew,
  ThumbUp,
} from '@mui/icons-material'
import Popover from '@mui/material/Popover'
import classNames from 'classnames'

import { useTranslate } from '@tolgee/react'
import { CopyToClipBoard } from 'helpers/clipboard'
import { useSearchParams } from 'react-router-dom'

const ActionButton = ({ onClick, isActive, children }) => (
  <div
    className={classNames(
      'group/action flex h-[28px] cursor-pointer items-center justify-center rounded-[4px] px-[5px] py-[3px]',
      {
        'bg-brand-violet-secondary hover:bg-brand-violet-tertiary': isActive,
        'lg:hover:bg-brand-form-accent-light hover:bg-white': !isActive,
      }
    )}
    onClick={onClick}
  >
    {children}
  </div>
)

const LibraryItemActions = ({
  item,
  handleRecommendChatbot,
  handleLikeChatbot,
  onPopoverOpen,
  onPopoverClose,
  isPopoverOpen,
  showChangeUserRoleModal,
  anchorEl,
  isDraft,
  secondary,
  setCreateChatbotModalData,
  handleDeleteChatBot,
}) => {
  const { t } = useTranslate()
  const setParams = useSearchParams()[1]

  const getIconClassName = (isActive, isPopoverOpen) =>
    classNames('cursor-pointer', {
      // 'text-bran-text-primary': isActive,
      'group-hover/action:text-brand-body text-brand-violet-secondary lg:text-brand-text-primary/60 lg:group-hover:text-brand-violet-secondary':
        !isActive,
      '!text-brand-violet-secondary': isPopoverOpen && !isActive,
    })

  const iconProps = {
    fontSize: 'inherit',
    sx: { height: '16px', width: '18px' },
  }

  const handleClick = (event) => {
    onPopoverOpen(event.currentTarget, item.id)
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={classNames(
        'flex items-center gap-[4px] rounded-[4px] bg-white/90 p-[4px] transition-all duration-300 lg:bg-transparent lg:group-hover:bg-white/90',
        {
          '!bg-white/90': isPopoverOpen,
        }
      )}
    >
      {!secondary && (
        <ActionButton onClick={() => handleLikeChatbot(item.id)} isActive={item.isLiked}>
          <Favorite
            {...iconProps}
            titleAccess={t('eleo-chatbot-library-like', 'Like it')}
            className={getIconClassName(item.isLiked, isPopoverOpen)}
          />
        </ActionButton>
      )}

      {!secondary && item.isRecommended && (
        <ActionButton onClick={() => handleRecommendChatbot(item.id)} isActive={item.isRecommended}>
          <Add
            {...iconProps}
            titleAccess={t('eleo-chatbot-library-recommend', 'Recommend')}
            className={getIconClassName(item.isRecommended, isPopoverOpen)}
          />
        </ActionButton>
      )}

      {secondary && (
        <ActionButton onClick={() => setParams({ edit: item.id }, { replace: true })}>
          <p
            className={classNames(
              getIconClassName(item.isLiked, isPopoverOpen),
              'text-[12px] font-medium capitalize'
            )}
          >
            {t('eleo-chatbot-library-edit-chatbot', 'Edit')}
          </p>
        </ActionButton>
      )}

      <ActionButton isActive={false} onClick={handleClick}>
        <MoreVert
          {...iconProps}
          sx={{ height: '18px', width: '18px' }}
          className={classNames(
            'group-hover/action:text-brand-body text-brand-violet-secondary lg:group-hover:text-brand-violet-secondary lg:text-brand-text-primary/60',
            {
              '!text-brand-violet-secondary': isPopoverOpen,
            }
          )}
        />
      </ActionButton>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={onPopoverClose}
        PaperProps={{
          className: ' rounded-[40px] pt-[7px] px-[8px] pb-[10px]',
          style: { boxShadow: 'rgba(0, 0, 0, 0.45) 1px 1px 5px 0px' },
        }}
        style={{ boxShadow: 'rgba(0, 0, 0, 0.45) 1px 1px 5px 0px' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div>
          {!isDraft && secondary && item?.settings?.publishing?.forum && (
            <div
              onClick={() => {
                window.open(
                  `${window.location.origin}/chatbot/chat/${item?.customUrls?.forum?.enableCustomUrl ? item.customUrls.forum?.customUrlName : item.id}`,
                  '_blank'
                )

                onPopoverClose()
              }}
              className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              <OpenInNew sx={{ height: '14px', width: '14px' }} />
              {t('eleo-chatbot-library-open-on-forum', 'Open on Forum')}
            </div>
          )}
          {!isDraft && secondary && item?.settings?.publishing?.forum && (
            <>
              <div
                onClick={() => {
                  CopyToClipBoard(
                    `${window.location.origin}/chatbot/chat/${item?.customUrls?.forum?.enableCustomUrl ? item.customUrls.forum?.customUrlName : item.id}`
                  )
                  onPopoverClose()
                }}
                className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
              >
                <ContentCopy sx={{ height: '14px', width: '14px' }} />
                {t('eleo-chatbot-library-copy-forum-link', 'Copy link to Forum')}
              </div>
              <div className='mx-2 my-2 border-b' />
            </>
          )}

          {!isDraft && secondary && item?.settings?.publishing?.public && (
            <div
              onClick={() => {
                window.open(
                  `${window.location.origin}/chat/${item?.customUrls?.public?.enableCustomUrl ? item.customUrls.public?.customUrlName : item.id}`,
                  '_blank'
                )

                onPopoverClose()
              }}
              className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              <OpenInNew sx={{ height: '14px', width: '14px' }} />
              {t('eleo-chatbot-library-open-on-public', 'Open direct link')}
            </div>
          )}
          {!isDraft && secondary && item?.settings?.publishing?.public && (
            <>
              <div
                onClick={() => {
                  CopyToClipBoard(
                    `${window.location.origin}/chat/${item?.customUrls?.public?.enableCustomUrl ? item.customUrls.public?.customUrlName : item.id}`
                  )
                  onPopoverClose()
                }}
                className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
              >
                <ContentCopy sx={{ height: '14px', width: '14px' }} />
                {t('eleo-chatbot-library-copy-public-link', 'Copy direct link')}
              </div>
              <div className='mx-2 my-2 border-b' />
            </>
          )}

          {!secondary && (
            <div
              onClick={() => {
                CopyToClipBoard(
                  `${window.location.origin}/chatbot/chat/${item?.customUrls?.public?.enableCustomUrl ? item.customUrls.public?.customUrlName : item.id}`
                )
                onPopoverClose()
              }}
              className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              <ContentCopy sx={{ height: '14px', width: '14px' }} />
              {t('eleo-chatbot-library-copy-link', 'Copy link')}
            </div>
          )}

          {secondary && (
            <div
              onClick={() => {
                handleDeleteChatBot(item.id, isDraft ? 'draft' : 'published')
                onPopoverClose()
              }}
              className='text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              <Delete sx={{ height: '14px', width: '14px' }} />
              {t('eleo-templates-delete', 'Delete')}
            </div>
          )}

          {!secondary && !item.isRecommended && (
            <div
              onClick={() => {
                handleRecommendChatbot(item.id)
                onPopoverClose()
              }}
              className=' text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              {item.isRecommended ? (
                <>
                  <CheckBox
                    {...iconProps}
                    sx={{ height: '14px', width: '14px' }}
                    titleAccess={t(
                      'eleo-chatbot-library-remove-from-recommended',
                      'Remove from recommended'
                    )}
                    className={getIconClassName(item.isRecommended, isPopoverOpen)}
                  />
                  {t('eleo-chatbot-library-remove-from-recommended', 'Remove from recommended')}
                </>
              ) : (
                <>
                  <Add
                    {...iconProps}
                    sx={{ height: '14px', width: '14px' }}
                    titleAccess={t('eleo-chatbot-library-recommend', 'Add to recommended')}
                    className={getIconClassName(item.isRecommended, isPopoverOpen)}
                  />
                  {t('eleo-chatbot-library-recommend', 'Add to recommended')}
                </>
              )}
            </div>
          )}
          {secondary && !isDraft && (
            <div
              onClick={() => {
                showChangeUserRoleModal(item.id)
                onPopoverClose()
              }}
              className='text-brand-violet-secondary hover:bg-brand-violet-semi_transparent flex cursor-pointer items-center gap-[6px] rounded-[4px] p-[6px] text-[12px] font-medium transition-all duration-300'
            >
              <ControlPointDuplicate
                {...iconProps}
                sx={{ height: '14px', width: '14px' }}
                titleAccess={t('eleo-chatbot-library-recommend', 'Recommend')}
                className={getIconClassName(item.isRecommended, isPopoverOpen)}
              />
              {t('eleo-chatbot-library-duplicate', 'Duplicate')}
            </div>
          )}
        </div>
      </Popover>
    </div>
  )
}

export default LibraryItemActions
